import logoOrangeSrc from "$/logoOrange.svg";
import footprintSrc from "$/footprint.svg";
import dogSrc from "$/dogT.svg";
import schoolSrc from "$/school.svg";
import hairShopSrc from "$/hairShop.svg";
import hotelSrc from "$/hotel.svg";

/**
 * PC(md+) 전용 좌·우 사이드 패널
 *  - VITE_APP_TYPE 환경변수로 모드(보호자/사업자) 결정
 *  - md(768px) 이하 모바일에서는 hidden
 *  - sticky top-0 h-screen 으로 스크롤해도 viewport에 고정
 *  - grid 컬럼 1(좌) / 3(우) 에 명시 배치
 *  - 좌측 : 브랜드 hero (로고 + 슬로건)
 *  - 우측 : 보조 정보 (TIP + 서비스 안내)
 */

type Mode = "customer" | "business";

const isBusiness = import.meta.env.VITE_APP_TYPE === "business";
const MODE: Mode = isBusiness ? "business" : "customer";

/* ─── 모드별 액센트 ─────────────────────────────────────── */
const ACCENT = isBusiness
  ? {
      // 사업자 — 보라/오렌지 믹스
      label: "text-[#7D6DFF]",
      brandText: "text-[#7D6DFF]",
      glowA: "bg-[#7D6DFF]",
      glowB: "bg-[#FFB87C]",
      cardBorder: "border-[#EDE9FF]",
      chip: "bg-[#7D6DFF]/10 text-[#7D6DFF]",
      title: "스마트한 멍하우스 운영",
      sub: "예약·알림장·정산을\n한 곳에서 관리하세요.",
      services: [
        { icon: schoolSrc, name: "유치원" },
        { icon: hairShopSrc, name: "미용" },
        { icon: hotelSrc, name: "호텔" },
        { icon: dogSrc, name: "데이케어" },
      ],
      tipLabel: "PRO TIP",
      tipText: "AI 알림장으로\n작성시간을 1/3로",
    }
  : {
      // 보호자 — 오렌지/노랑
      label: "text-[#FF9934]",
      brandText: "text-[#FF9934]",
      glowA: "bg-[#FFC97A]",
      glowB: "bg-[#FFB87C]",
      cardBorder: "border-[#FFE9D1]",
      chip: "bg-[#FFF1E0] text-[#FF8C2A]",
      title: "오늘도 우리 멍과 함께",
      sub: "사랑하는 반려동물의 일상을\n가까운 사업장과 안전하게.",
      services: [
        { icon: schoolSrc, name: "유치원" },
        { icon: hairShopSrc, name: "미용" },
        { icon: hotelSrc, name: "호텔" },
        { icon: dogSrc, name: "데이케어" },
      ],
      tipLabel: "MUNG TIP",
      tipText: "가까운 멍하우스를\n바로 둘러보세요.",
    };

/* ─── 공용 사이드 컨테이너 ──────────────────────────────── */
const SidePanelShell = ({
  side,
  children,
}: {
  side: "left" | "right";
  children: React.ReactNode;
}) => (
  <aside
    className={[
      "hidden md:flex relative flex-col items-center justify-center",
      "w-full max-w-[420px] mx-auto p-10",
      "md:h-full md:overflow-hidden",
      "md:row-start-1",
      side === "left" ? "md:col-start-1" : "md:col-start-3",
    ].join(" ")}
  >
    {/* 글로우 — 화면 바깥쪽으로만 번짐 (중앙 침범 X) */}
    <div
      className={`absolute -top-24 ${side === "left" ? "-left-20" : "-right-20"} w-[360px] h-[360px] rounded-full ${ACCENT.glowA} opacity-25 blur-3xl pointer-events-none`}
    />
    <div
      className={`absolute -bottom-32 ${side === "left" ? "-left-10" : "-right-10"} w-[320px] h-[320px] rounded-full ${ACCENT.glowB} opacity-25 blur-3xl pointer-events-none`}
    />
    {children}
  </aside>
);

/* ─── 좌측 (Hero) ─────────────────────────────────────── */
const LeftPanel = () => (
  <SidePanelShell side="left">
    {/* 발자국 데코 */}
    <img
      src={footprintSrc}
      alt=""
      aria-hidden
      className="absolute top-12 left-8 w-12 opacity-25 -rotate-12 pointer-events-none"
    />
    <img
      src={footprintSrc}
      alt=""
      aria-hidden
      className="absolute bottom-20 left-14 w-10 opacity-20 rotate-12 pointer-events-none"
    />

    <div className="relative w-full max-w-[300px] flex flex-col items-center text-center">
      <div className={`text-[10px] tracking-[0.25em] font-bold mb-4 ${ACCENT.label}`}>
        {MODE === "business" ? "FOR BUSINESS" : "FOR PET OWNER"}
      </div>

      {/* 로고 + 동그란 프레임 */}
      <div className="relative w-32 h-32 rounded-full bg-white shadow-xl flex items-center justify-center mb-6 border border-[#FFE9D1]">
        <img src={logoOrangeSrc} alt="MUNG" className="w-16 h-16 object-contain" />
        <div className="absolute -bottom-2 -right-1 bg-white rounded-full px-2.5 py-1 shadow-md text-[10px] font-bold text-[#FF8C2A] border border-[#FFE9D1]">
          🐾 멍투데이
        </div>
      </div>

      <div className="text-xl font-bold text-[#111] leading-snug mb-2 whitespace-pre-line">
        {ACCENT.title}
      </div>
      <div className="text-xs text-[#666] leading-relaxed whitespace-pre-line">
        {ACCENT.sub}
      </div>
    </div>
  </SidePanelShell>
);

/* ─── 우측 (TIP + Service) ─────────────────────────────── */
const RightPanel = () => (
  <SidePanelShell side="right">
    {/* 발자국 데코 */}
    <img
      src={footprintSrc}
      alt=""
      aria-hidden
      className="absolute top-16 right-8 w-10 opacity-20 rotate-12 pointer-events-none"
    />
    <img
      src={footprintSrc}
      alt=""
      aria-hidden
      className="absolute bottom-24 right-12 w-12 opacity-25 -rotate-12 pointer-events-none"
    />

    <div className="relative w-full max-w-[300px] flex flex-col gap-3">
      {/* TIP 카드 */}
      <div className={`bg-white rounded-2xl p-5 shadow-md border ${ACCENT.cardBorder}`}>
        <div className={`inline-block px-2 py-0.5 rounded-md text-[10px] font-bold mb-2 ${ACCENT.chip}`}>
          {ACCENT.tipLabel}
        </div>
        <div className="text-sm font-semibold text-[#111] leading-snug whitespace-pre-line">
          {ACCENT.tipText}
        </div>
      </div>

      {/* 서비스 그리드 (2x2) */}
      <div className="bg-white rounded-2xl p-4 shadow-md border border-[#F0F0F0]">
        <div className="text-[10px] tracking-widest text-[#999] font-bold mb-3 text-center">
          OUR SERVICE
        </div>
        <div className="grid grid-cols-2 gap-2">
          {ACCENT.services.map((s) => (
            <div
              key={s.name}
              className="flex flex-col items-center text-center py-3 rounded-xl bg-[#FAFAFA] hover:bg-white hover:shadow-sm transition"
            >
              <img src={s.icon} alt="" className="w-8 h-8 object-contain mb-1" />
              <div className="text-[11px] font-semibold text-[#111]">{s.name}</div>
            </div>
          ))}
        </div>
      </div>

      <div className="text-center text-[10px] text-[#999]">
        모바일 앱으로 더 편리하게
      </div>
    </div>
  </SidePanelShell>
);

/* ─── export ─────────────────────────────────────────── */
export const SidePanels = () => (
  <>
    <LeftPanel />
    <RightPanel />
  </>
);
